import { createContext, useContext } from 'react';
import AuthContext, { useAuth } from '../contexts/AuthContext';
import type { AuthUser } from '../services/authService';

/**
 * Admin-Erkennung an EINER Stelle (2026-09-28, Marcel): Rolle 'ADMIN' (Groß-/Kleinschreibung egal).
 * PURE – testbar ohne React.
 */
export const isAdminUser = (user: Pick<AuthUser, 'role'> | null | undefined): boolean =>
  String(user?.role ?? '').trim().toUpperCase() === 'ADMIN';

/** Ersatz-Context, falls das AuthContext-Modul (z. B. in Tests) keinen Default-Export liefert */
const FallbackContext = createContext<{ user?: AuthUser | null } | undefined>(undefined);

/** useAuth() fail-soft: ohne Provider (oder ohne Mock) → undefined statt Fehler */
const useAuthSafe = (): { user?: AuthUser | null } | undefined => {
  try {
    return useAuth();
  } catch {
    return undefined;
  }
};

/**
 * Hook: ist der eingeloggte Nutzer Admin? Fail-soft – ohne AuthProvider (z. B. isolierte
 * Komponenten-Tests, Vorschau) immer false, damit nie Admin-Elemente „aus Versehen“ erscheinen.
 * Liest den Context direkt UND über useAuth(), damit auch Tests funktionieren, die nur useAuth mocken.
 */
export function useIsAdmin(): boolean {
  const ctx = useContext(AuthContext ?? FallbackContext);
  const viaHook = useAuthSafe();
  return isAdminUser(ctx?.user ?? viaHook?.user);
}

export default useIsAdmin;
